<script setup>

import { useDisplay } from 'vuetify'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from '@fullcalendar/list';
import allLocales from '@fullcalendar/core/locales-all';
import interactionPlugin from '@fullcalendar/interaction';
import {useI18n} from "vue-i18n";

const { xs } = useDisplay()
const router = useRouter()
const { locale } = useI18n({ useScope: 'global' })

const props = defineProps({
  events: {
    type: Object,
    required: false
  },
  initialView: {
    type: String,
    required: true
  },
  newView: {
    type: String,
    required: false,
    default: 'dayGridMonth'
  }
});

const emit = defineEmits(['view-change'])
const calendarRef = ref(null);

onMounted(() => {
  if(xs.value) {
    calendarOptions.value.headerToolbar = {
      left: '', // Buttons for navigating through the calendar
      center: 'title',          // The current date or date range
      right: '', // Buttons to switch views
    };
    calendarOptions.value.buttonText = {
    };
  }
})
const calendarOptions = ref({
  plugins: [ dayGridPlugin, timeGridPlugin, listPlugin,interactionPlugin ],
  initialView: props.initialView,
  displayEventTime: false,
  locales: allLocales,
  locale: locale,
  eventContent: function(arg) {
    // Check if event spans multiple days
    if (arg.view.type !== "yearList") {
      return true;
    }

    const start = arg.event.start;
    const end = arg.event.end;
    const title = arg.event.title;

    if(title.startsWith("time-off - ")) {
      return {
        html: `<div>&nbsp;</div>`
      };
    }

    if (start && arg.event.extendedProps.overrideExitDate) {
      const override = new Date(arg.event.extendedProps.overrideExitDate);
      return {
        html: `<div>${arg.event.title} (${start.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false })} - ${override.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false })})</div>`
      };
    }

    if (start && end && start.toDateString() !== end.toDateString()) {
      // Custom rendering for multi-day events
      return {
        html: `<div>${arg.event.title} (${start.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false })} - ${end.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false })})</div>`
      };
    }

    if (start && end) {
      // Custom rendering for multi-day events
      return {
        html: `<div>${arg.event.title} (${start.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false }) } - ${end.toLocaleTimeString([], {hour: '2-digit',minute: '2-digit',hour12: false }) })</div>`
      };
    }

    // Use default rendering as fallbacks
    return true;
  },
  listDayFormat: {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  },

  eventClick: function(info) {
    const cellEl = info.el.closest('[data-date]');
    let dateString;
    if (cellEl) {
      // Use the calendar cell date (handles night shifts correctly)
      dateString = cellEl.dataset.date.replace(/-/g, '');
    } else {
      // Fallback for list view where events are not inside day-cells
      const start = info.event.start;
      const year = start.getFullYear();
      const month = String(start.getMonth() + 1).padStart(2, '0');
      const day = String(start.getDate()).padStart(2, '0');
      dateString = `${year}${month}${day}`;
    }
    router.push(`/roster/details/${dateString}`);
  },
  dateClick: function(info) {
    const year = info.date.getFullYear();
    const month = String(info.date.getMonth() + 1).padStart(2, '0');
    const day = String(info.date.getDate()).padStart(2, '0');
    const dateString = `${year}${month}${day}`
    router.push(`/roster/details/${dateString}`)
  },
  datesSet: (dateInfo) => {
    emit('view-change', dateInfo.view.type);
  },
  views: {
    yearList: {
      type: 'list',
      duration: { days: 365 }, // Assuming non-leap year or adjust accordingly
      buttonText: 'List',
      visibleRange: function(currentDate) {
        // Starting at the beginning of the current year
        const start = new Date(currentDate.getFullYear(), 0, 1);
        const end = new Date(currentDate.getFullYear() + 1, 0, 1);
        return { start, end };
      },
      headerToolbar: {
        right: 'dayGridMonth,timeGridWeek,yearList', // Buttons to switch views
      },
    }
  },
  headerToolbar: {
    left: 'prev,next today', // Buttons for navigating through the calendar
    center: 'title',          // The current date or date range
    right: 'dayGridMonth,timeGridWeek,yearList', // Buttons to switch views
  },
  buttonText: {
    today: 'Today',
    month: 'Month',
    week: 'Week',
    year: 'List'
  },
  events: [],  // No events to start with
  eventDisplay: 'block',
  eventColor: 'rgb(var(--v-global-theme-primary))',
  eventTextColor: 'rgb(var(--v-theme-on-primary))'
});

function convertObjectToFullCalendarEvents(eventsObject) {
  return Object.keys(eventsObject).map(key => {
    const shift = eventsObject[key];
    return {
      id: shift.id,
      title: `${shift.EntryType} - ${shift.shiftGroupDetails.location}`,
      start: shift.StartTime,
      end: shift.EndTime,
      allDay: false,
      extendedProps: {
        shiftGroup: shift.ShiftGroup,
        type: shift.Type,
        user: shift.User,
        remark: shift.Remark,
        shiftGroupDetails: shift.shiftGroupDetails,
        overrideExitDate: shift.overrideExitDate
      }
    };
  });
}

// Watch props.events for changes and update calendar events
watch(() => props.events, () => {
  calendarOptions.value.events = convertObjectToFullCalendarEvents(props.events);
}, {
    deep: true
});

// Watch props.newView and change the view
watch(
    () => props.newView,
    (newView) => {
      if (newView && calendarRef.value) { // Check if calendarRef is available
        const calendarApi = calendarRef.value.getApi();
        calendarApi.changeView(newView);
        console.log("CALENDAR: changed view to: " + newView)
      }
    }
);

</script>

<template>
  <VRow class="fill-height">
    <VCol>
      <FullCalendar
        ref="calendarRef"
        :options="calendarOptions"
      />
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/libs/full-calendar";

.calendars-checkbox {
    .v-label {
        color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
        opacity: var(--v-high-emphasis-opacity);
    }
}

.calendar-add-event-drawer {
    &.v-navigation-drawer:not(.v-navigation-drawer--temporary) {
        border-end-start-radius: 0.375rem;
        border-start-start-radius: 0.375rem;
    }
}
</style>
